/**
 * Dispute Wizard System States
 * 
 * Premium loading, empty, error, and locked review states.
 * These should feel calm and premium, not broken or alarming.
 */

import React from 'react';
import {
  Loader2,
  FileSearch,
  AlertCircle,
  Lock,
  RefreshCw,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// ============================================
// LOADING STATE
// ============================================

export const LoadingState = ({ message = 'Analyzing report...' }) => {
  const steps = [
    'Analyzing report',
    'Classifying issue',
    'Evaluating safest path',
    'Preparing recommendation',
  ];

  return (
    <div 
      className="flex flex-col items-center justify-center py-16 px-8"
      data-testid="dispute-wizard-loading-state"
    >
      <div className="relative mb-6">
        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
          <Loader2 size={28} className="text-white animate-spin" />
        </div>
        <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center">
          <RefreshCw size={12} className="text-amber-900" />
        </div>
      </div>
      
      <h3 className="text-xl font-bold text-stone-900 mb-2">Processing Your File</h3>
      <p className="text-slate-600 text-center max-w-md mb-6">{message}</p>
      
      <div className="space-y-2 w-full max-w-xs">
        {steps.map((step, index) => (
          <div 
            key={index}
            className="flex items-center gap-3 text-sm"
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            <div className="w-5 h-5 rounded-full border-2 border-emerald-500 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            </div>
            <span className="text-slate-600">{step}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// EMPTY STATE
// ============================================

export const EmptyState = ({ onStartFlow }) => {
  return (
    <div 
      className="flex flex-col items-center justify-center py-16 px-8"
      data-testid="dispute-wizard-empty-state"
    >
      <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mb-6">
        <FileSearch size={28} className="text-slate-400" />
      </div>
      
      <h3 className="text-xl font-bold text-stone-900 mb-2">No Issue Detected Yet</h3>
      <p className="text-slate-600 text-center max-w-md mb-6">
        Upload a report or begin a guided request workflow to let the system identify the right path.
      </p>
      
      <Button 
        onClick={onStartFlow}
        className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
      >
        Start Guided Intake
        <ArrowRight size={16} className="ml-2" />
      </Button>
    </div>
  );
};

// ============================================
// ERROR STATE
// ============================================

export const ErrorState = ({ onRetry, onHumanReview }) => {
  return (
    <div 
      className="flex flex-col items-center justify-center py-16 px-8"
      data-testid="dispute-wizard-error-state"
    >
      <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
        <AlertCircle size={28} className="text-amber-600" />
      </div>
      
      <h3 className="text-xl font-bold text-stone-900 mb-2">We Could Not Complete Analysis</h3>
      <p className="text-slate-600 text-center max-w-md mb-6">
        Please retry the upload or continue with Human Review if you want a safer manual routing path.
      </p>
      
      <div className="flex gap-3">
        <Button 
          onClick={onRetry}
          className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
        >
          <RefreshCw size={16} className="mr-2" />
          Retry Analysis
        </Button>
        <Button 
          variant="outline"
          onClick={onHumanReview}
        >
          Open Human Review
        </Button>
      </div>
    </div>
  );
};

// ============================================
// LOCKED REVIEW STATE
// ============================================

export const LockedReviewState = ({ triggers = [], onViewDetails, onSendToReview }) => {
  return (
    <div 
      className="border-2 border-red-200 bg-gradient-to-br from-red-50 to-white rounded-2xl p-8"
      data-testid="dispute-wizard-locked-state"
    >
      <div className="flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-6">
          <Lock size={28} className="text-red-600" />
        </div>
        
        <h3 className="text-xl font-bold text-red-900 mb-2">Human Review Required</h3>
        <p className="text-red-800 max-w-lg mb-6">
          This file needs additional review before generation can continue. 
          The system found contradiction or escalation signals that should not be pushed through default automation.
        </p>
        
        {triggers.length > 0 && (
          <div className="bg-red-100/50 border border-red-200 rounded-xl p-4 w-full max-w-md mb-6">
            <p className="text-xs text-red-700 font-semibold uppercase tracking-wide mb-2">
              Detected Triggers
            </p>
            <ul className="space-y-2">
              {triggers.map((trigger, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-red-800">
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <span>{trigger}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        <div className="flex gap-3">
          <Button 
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            onClick={onViewDetails}
          >
            View Detection Details
          </Button>
          <Button 
            className="bg-red-600 hover:bg-red-700 text-white"
            onClick={onSendToReview}
          >
            Send To Human Review
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// STATES DEMO PAGE (For development/preview)
// ============================================

const SystemStatesDemo = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-6 space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 mb-2">System States</h1>
          <p className="text-slate-600">Premium loading, empty, error, and locked states for the Dispute Wizard.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Loading State</span>
            </div>
            <LoadingState />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Empty State</span>
            </div>
            <EmptyState onStartFlow={() => {}} />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Error State</span>
            </div>
            <ErrorState onRetry={() => {}} onHumanReview={() => {}} />
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
              <span className="text-sm font-semibold text-slate-700">Locked Review State</span>
            </div>
            <div className="p-4">
              <LockedReviewState 
                triggers={[
                  'Source conflict detected',
                  'Low-confidence classification',
                  'Possible wrong association risk',
                ]}
                onViewDetails={() => {}}
                onSendToReview={() => {}}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatesDemo;
